import React from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, RefreshCcw } from 'lucide-react'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../../config/api'

const MAX_PREVIEW = 5

function normalizeCaseType(caseType) {
  return String(caseType || '').toUpperCase()
}

export function caseDetailHref(c) {
  const caseType = normalizeCaseType(c?.case_type)
  const id = c?.id
  if (!id) return null
  if (caseType === 'ECDD') return `/compliance-safe/ecdd/${encodeURIComponent(id)}`
  if (caseType === 'SMR' || caseType === 'SUSPICIOUS') {
    return `/compliance-safe/investigations/${encodeURIComponent(id)}?tab=smr_form`
  }
  if (caseType === 'INVESTIGATION') {
    return `/compliance-safe/investigations/${encodeURIComponent(id)}`
  }
  return null
}

function relatedCaseHref(relatedCaseId) {
  const id = String(relatedCaseId || '').trim()
  if (!id) return null
  return `/compliance-safe/investigations/${encodeURIComponent(id)}`
}

function formatDateTime(value) {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
}

function caseTypeBadgeClass(caseType) {
  const t = normalizeCaseType(caseType)
  if (t === 'SMR' || t === 'SUSPICIOUS') return 'bg-amber-100 text-amber-800 border-amber-200'
  if (t === 'ECDD') return 'bg-blue-100 text-blue-800 border-blue-200'
  if (t === 'INVESTIGATION') return 'bg-teal-100 text-teal-800 border-teal-200'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

const INCIDENT_TYPE_LABELS = {
  third_party: 'Third-party collection',
  structuring: 'Structuring / smurfing',
  minimal_play: 'Minimal play / cash conversion',
  suspicious: 'Suspicious behaviour',
  counterfeit_fraud: 'Counterfeit / fraud',
  self_exclusion: 'Self-exclusion breach',
  intoxication: 'Intoxication',
  underage: 'Underage',
  other: 'Other concern',
  no_incident: 'No incident to report',
}

function incidentTypeLabel(type) {
  const t = String(type || '').trim()
  if (!t) return 'Incident report'
  return INCIDENT_TYPE_LABELS[t] || t.replace(/_/g, ' ')
}

function incidentHref(inc) {
  const linked = inc?.linked_case_id || inc?.linkedCaseId
  if (linked && String(linked).trim()) {
    return `/compliance-safe/investigations/${encodeURIComponent(String(linked).trim())}`
  }
  const id = inc?.id
  if (id) return `/compliance-safe/incidents?highlight=${encodeURIComponent(String(id))}`
  return '/compliance-safe/incidents'
}

function alertHref(alert) {
  const id = alert?.id
  if (id == null) return null
  return `/monitor-safe/alerts/${encodeURIComponent(String(id))}`
}

function alertSeverityClass(severity) {
  const s = String(severity || '').toLowerCase()
  if (s === 'critical') return 'bg-red-100 text-red-800 border-red-200'
  if (s === 'high') return 'bg-orange-100 text-orange-800 border-orange-200'
  if (s === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-slate-100 text-slate-800 border-slate-200'
}

function kycReasonLabel(reason) {
  const r = String(reason || '').trim()
  if (!r) return 'KYC'
  return r.replace(/_/g, ' ')
}

function groupCases(caseHistory) {
  const groups = { ecdd: [], smr: [], investigation: [], other: [] }
  for (const c of caseHistory || []) {
    const t = normalizeCaseType(c.case_type)
    if (t === 'ECDD') groups.ecdd.push(c)
    else if (t === 'SMR' || t === 'SUSPICIOUS') groups.smr.push(c)
    else if (t === 'INVESTIGATION') groups.investigation.push(c)
    else groups.other.push(c)
  }
  return groups
}

function ComplianceRow({ badge, badgeClass, title, meta, href, date }) {
  return (
    <div className={`flex items-center justify-between border rounded-lg p-3 gap-3 ${href ? 'hover:bg-gray-50' : ''}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Badge className={`shrink-0 ${badgeClass}`}>{badge}</Badge>
        {href ? (
          <Link to={href} className="font-medium text-sm text-primary hover:underline flex items-center gap-1 truncate">
            <span className="truncate">{title}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </Link>
        ) : (
          <span className="font-medium text-sm truncate">{title}</span>
        )}
        {meta && <span className="text-xs text-muted-foreground truncate">{meta}</span>}
      </div>
      <div className="text-right text-xs text-gray-500 shrink-0">{formatDateTime(date)}</div>
    </div>
  )
}

function ComplianceSection({ title, count, viewAllHref, viewAllLabel, emptyText, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-800">{title} ({count})</h4>
        {count > 0 && viewAllHref && (
          <Link to={viewAllHref} className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
            {viewAllLabel || 'View all'}
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      {count === 0 ? (
        <p className="text-sm text-gray-500 pl-1">{emptyText}</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  )
}

export function MemberComplianceRecord({
  memberId,
  membershipNumber,
  caseHistory = [],
  kycEntries = [],
  incidents = [],
  monitorAlerts = [],
  amlCount = 0,
  isLoadingCases = false,
  isLoadingKyc = false,
  isLoadingIncidents = false,
  isLoadingAlerts = false,
}) {
  const registryMemberId = membershipNumber || memberId
  const kycRegistryHref = registryMemberId
    ? `/compliance-safe/kyc-registry?member_id=${encodeURIComponent(registryMemberId)}`
    : '/compliance-safe/kyc-registry'

  const groups = groupCases(caseHistory)
  const ongoing = caseHistory.filter((c) =>
    ['OPEN', 'INVESTIGATING', 'PENDING_REVIEW', 'SUSPICION_FORMED', 'SMR_DRAFT', 'PENDING_APPROVAL'].includes(
      String(c.status || '').toUpperCase()
    )
  ).length

  const loading = isLoadingCases || isLoadingKyc || isLoadingIncidents || isLoadingAlerts
  const activityIncidents = (incidents || []).filter(
    (inc) => String(inc?.incident_type || '').trim() !== 'no_incident'
  )
  const incidentsHref = registryMemberId
    ? `/compliance-safe/incidents?member_id=${encodeURIComponent(registryMemberId)}`
    : '/compliance-safe/incidents'
  const alertsHref = registryMemberId
    ? `/monitor-safe/alerts?q=${encodeURIComponent(registryMemberId)}`
    : '/monitor-safe/alerts'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Member compliance record</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {ongoing > 0 ? `${ongoing} ongoing case${ongoing === 1 ? '' : 's'}` : 'Linked KYC, ECDD, SMR, and investigations'}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCcw className="h-4 w-4 animate-spin" />
            Loading compliance history…
          </div>
        )}

        <ComplianceSection
          title="KYC performed"
          count={kycEntries.length}
          viewAllHref={kycRegistryHref}
          viewAllLabel="KYC Registry"
          emptyText="No KYC registry entries for this member."
        >
          {kycEntries.slice(0, MAX_PREVIEW).map((k) => {
            const relatedHref = relatedCaseHref(k.related_case_id)
            const title = `KYC #${k.id} — ${kycReasonLabel(k.reason)}`
            const meta = k.payout_amount
              ? `$${Number(k.payout_amount).toLocaleString()}`
              : k.related_case_id
                ? `Case ${k.related_case_id}`
                : null
            return (
              <ComplianceRow
                key={`kyc-${k.id}`}
                badge="KYC"
                badgeClass="bg-violet-100 text-violet-800 border-violet-200"
                title={title}
                meta={relatedHref ? null : meta}
                href={relatedHref || kycRegistryHref}
                date={k.performed_at || k.created_at}
              />
            )
          })}
        </ComplianceSection>

        <ComplianceSection
          title="ECDD"
          count={groups.ecdd.length}
          emptyText="No ECDD cases on record."
        >
          {groups.ecdd.slice(0, MAX_PREVIEW).map((c) => (
            <ComplianceRow
              key={`ecdd-${c.id}`}
              badge="ECDD"
              badgeClass={caseTypeBadgeClass(c.case_type)}
              title={c.case_number || c.id}
              meta={c.status}
              href={caseDetailHref(c)}
              date={c.created_at}
            />
          ))}
        </ComplianceSection>

        <ComplianceSection
          title="SMR"
          count={groups.smr.length}
          emptyText="No SMR cases on record."
        >
          {groups.smr.slice(0, MAX_PREVIEW).map((c) => (
            <ComplianceRow
              key={`smr-${c.id}`}
              badge="SMR"
              badgeClass={caseTypeBadgeClass(c.case_type)}
              title={c.case_number || c.id}
              meta={c.status}
              href={caseDetailHref(c)}
              date={c.created_at}
            />
          ))}
        </ComplianceSection>

        <ComplianceSection
          title="UAR / incident reports"
          count={activityIncidents.length}
          viewAllHref={incidentsHref}
          viewAllLabel="Incident monitoring"
          emptyText="No UAR or incident reports linked to this member."
        >
          {activityIncidents.slice(0, MAX_PREVIEW).map((inc) => {
            const type = String(inc.incident_type || '').trim()
            const title = `${inc.id || 'Report'} — ${incidentTypeLabel(type)}`
            const meta = inc.severity
              ? `${String(inc.severity).toUpperCase()} · ${inc.status || 'open'}`
              : inc.status || null
            return (
              <ComplianceRow
                key={`inc-${inc.id}`}
                badge="UAR"
                badgeClass="bg-yellow-100 text-yellow-800 border-yellow-200"
                title={title}
                meta={meta}
                href={incidentHref(inc)}
                date={inc.created_at || inc.reported_at}
              />
            )
          })}
        </ComplianceSection>

        <ComplianceSection
          title="Monitor alerts"
          count={monitorAlerts.length}
          viewAllHref={alertsHref}
          viewAllLabel="Alert queue"
          emptyText="No rule alerts for this member."
        >
          {monitorAlerts.slice(0, MAX_PREVIEW).map((a) => (
            <ComplianceRow
              key={`alert-${a.id}`}
              badge="Alert"
              badgeClass={alertSeverityClass(a.severity)}
              title={a.rule_name || `Alert #${a.id}`}
              meta={a.status || a.category || null}
              href={alertHref(a)}
              date={a.first_seen || a.last_updated || a.created_at}
            />
          ))}
        </ComplianceSection>

        <ComplianceSection
          title="Investigations"
          count={groups.investigation.length}
          emptyText="No investigation cases on record."
        >
          {groups.investigation.slice(0, MAX_PREVIEW).map((c) => (
            <ComplianceRow
              key={`inv-${c.id}`}
              badge="Investigation"
              badgeClass={caseTypeBadgeClass(c.case_type)}
              title={c.case_number || c.id}
              meta={c.status}
              href={caseDetailHref(c)}
              date={c.created_at}
            />
          ))}
        </ComplianceSection>

        <div className="border-t pt-4 flex flex-wrap gap-3 text-sm">
          <span className="text-gray-600">
            AML/PEP checks on this page: <strong>{amlCount}</strong>
          </span>
          {registryMemberId && (
            <Link to={kycRegistryHref} className="text-primary font-medium hover:underline flex items-center gap-1">
              Open KYC Registry (filtered)
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function buildTimelineEvents(caseHistory, kycEntries, amlChecks, incidents, monitorAlerts) {
  const events = []

  for (const inc of incidents || []) {
    if (String(inc?.incident_type || '').trim() === 'no_incident') continue
    events.push({
      id: `inc-${inc.id}`,
      at: inc.created_at || inc.reported_at,
      type: 'UAR',
      badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: incidentTypeLabel(inc.incident_type),
      sub: inc.id ? `Report ${inc.id}` : null,
      href: incidentHref(inc),
    })
  }

  for (const a of monitorAlerts || []) {
    events.push({
      id: `alert-${a.id}`,
      at: a.first_seen || a.last_updated || a.created_at,
      type: 'Alert',
      badgeClass: alertSeverityClass(a.severity),
      label: a.rule_name || `Alert #${a.id}`,
      sub: a.status || a.severity || null,
      href: alertHref(a),
    })
  }

  for (const k of kycEntries || []) {
    events.push({
      id: `kyc-${k.id}`,
      at: k.performed_at || k.created_at,
      type: 'KYC',
      badgeClass: 'bg-violet-100 text-violet-800 border-violet-200',
      label: `${kycReasonLabel(k.reason)}${k.payout_amount ? ` · $${Number(k.payout_amount).toLocaleString()}` : ''}`,
      sub: k.related_case_id ? `Related case ${k.related_case_id}` : k.member_name || null,
      href: relatedCaseHref(k.related_case_id) || (k.id ? `/compliance-safe/kyc-registry?member_id=${encodeURIComponent(k.member_id || '')}` : null),
    })
  }

  for (const c of caseHistory || []) {
    const t = normalizeCaseType(c.case_type)
    const badge = t === 'ECDD' ? 'ECDD' : t === 'SMR' || t === 'SUSPICIOUS' ? 'SMR' : t === 'INVESTIGATION' ? 'Investigation' : c.case_type || 'Case'
    events.push({
      id: `case-${c.id}`,
      at: c.created_at,
      type: badge,
      badgeClass: caseTypeBadgeClass(c.case_type),
      label: c.case_number || String(c.id),
      sub: c.status || null,
      href: caseDetailHref(c),
    })
  }

  for (const a of amlChecks || []) {
    events.push({
      id: `aml-${a.id}`,
      at: a.created_at,
      type: 'AML',
      badgeClass: 'bg-slate-100 text-slate-800 border-slate-200',
      label: `AML check #${a.id}`,
      sub: a.reason || (a.result_count != null ? `${a.result_count} result(s)` : null),
      href: null,
      amlId: a.id,
    })
  }

  return events
    .filter((e) => e.at)
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
}

export function MemberActivityTimeline({
  caseHistory = [],
  kycEntries = [],
  amlChecks = [],
  incidents = [],
  monitorAlerts = [],
  onViewAmlCheck,
  isLoading = false,
  maxItems = 25,
}) {
  const events = buildTimelineEvents(
    caseHistory,
    kycEntries,
    amlChecks,
    incidents,
    monitorAlerts,
  ).slice(0, maxItems)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity timeline</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Chronological view of UARs, alerts, KYC, cases, and AML checks
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <RefreshCcw className="h-4 w-4 animate-spin" />
            Loading activity…
          </div>
        )}
        {events.length === 0 && !isLoading ? (
          <p className="text-sm text-gray-500">No compliance activity recorded yet.</p>
        ) : (
          <ol className="relative border-l border-gray-200 ml-2 space-y-4">
            {events.map((e) => (
              <li key={e.id} className="ml-4">
                <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-gray-300 border-2 border-white" />
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`text-xs shrink-0 ${e.badgeClass}`}>{e.type}</Badge>
                    {e.href ? (
                      <Link to={e.href} className="text-sm font-medium text-primary hover:underline truncate">
                        {e.label}
                      </Link>
                    ) : e.amlId && onViewAmlCheck ? (
                      <button
                        type="button"
                        onClick={() => onViewAmlCheck(e.amlId)}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {e.label}
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{e.label}</span>
                    )}
                  </div>
                  <time className="text-xs text-gray-500 shrink-0">{formatDateTime(e.at)}</time>
                </div>
                {e.sub && <p className="text-xs text-muted-foreground mt-0.5">{e.sub}</p>}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

/** Load KYC registry rows for a member (club # or player id). */
export async function fetchKycEntriesForMember(memberKeys) {
  const keys = [...new Set((memberKeys || []).map((k) => String(k).trim()).filter(Boolean))]
  const merged = []
  const seen = new Set()

  for (const key of keys) {
    try {
      const qs = new URLSearchParams({ member_id: key, limit: '50' })
      const res = await authFetch(getLocalApiUrl(`/api/kyc-registry?${qs}`))
      const json = await res.json()
      if (!json.success || !Array.isArray(json.data)) continue
      for (const row of json.data) {
        const id = row.id
        if (id != null && !seen.has(id)) {
          seen.add(id)
          merged.push(row)
        }
      }
    } catch (_) {
      /* ignore per-key failures */
    }
  }

  return merged.sort((a, b) => {
    const ta = new Date(a.performed_at || a.created_at || 0).getTime()
    const tb = new Date(b.performed_at || b.created_at || 0).getTime()
    return tb - ta
  })
}

/** UAR / incident reports (same table as mobile floor UAR). */
export async function fetchIncidentsForMember(memberId) {
  const mid = String(memberId || '').trim()
  if (!mid) return []
  try {
    const res = await authFetch(
      getLocalApiUrl(`/api/mobile/incidents?member_id=${encodeURIComponent(mid)}&limit=50`)
    )
    const json = await res.json()
    if (!res.ok || !json.success) return []
    return Array.isArray(json.data) ? json.data : []
  } catch (_) {
    return []
  }
}

/** Monitor-safe rule alerts for this member. */
export async function fetchMonitorAlertsForMember(memberId) {
  const mid = String(memberId || '').trim()
  if (!mid) return []
  try {
    const res = await authFetch(
      getLocalApiUrl(`/api/alerts/list?member_id=${encodeURIComponent(mid)}&limit=50`)
    )
    const json = await res.json()
    if (!res.ok || !json.success) return []
    const data = json.data
    return Array.isArray(data) ? data : []
  } catch (_) {
    return []
  }
}
